// Mandatory first-run disclosures (PRD §6.7 + UX §4.1).
//
// The text below is the audit anchor — its SHA-256 is recorded with
// the disclosures_acknowledged event. Future updates to disclosure
// text trigger a re-acknowledgment requirement (UX §4.1 persistence
// rule).

export type Disclosure = {
  num: number;
  title: string;
  body: string;
};

export const DISCLOSURES: Disclosure[] = [
  {
    num: 1,
    title: "Trading involves substantial risk of loss.",
    body:
      "Most retail day traders lose money. The base rate of profitability for systematic retail day traders over a multi-year period is well below 50%. This system does not change that base rate.",
  },
  {
    num: 2,
    title: "The AI is a research aid, not an authority.",
    body:
      "AI confidence scores reflect pattern matching, not market truth. Every trade decision is yours.",
  },
  {
    num: 3,
    title: "Past performance does not predict future results.",
    body:
      "Backtest results have inherent limitations, including survivorship bias, look-ahead, and slippage assumptions that may not match live execution.",
  },
  {
    num: 4,
    title: "The system can fail.",
    body:
      "Software, network, broker, and data provider failures will happen. You should not rely on automated stops to fully protect capital. Have a plan for system outages.",
  },
  {
    num: 5,
    title: "This is not financial advice.",
    body:
      "Consult a licensed advisor before making investment decisions. The author of this system is not your fiduciary and accepts no liability for losses incurred from its use.",
  },
  {
    num: 6,
    title: "Project exit criteria.",
    body:
      "If after 12 months your live results trail the risk-free rate, the system will recommend you stop. (PRD §2.4)",
  },
];

// Canonical text used as the audit hash input. Stable across runs so
// the same disclosure text always produces the same hash.
export const canonicalDisclosureText = (): string =>
  DISCLOSURES.map((d) => `${d.num}. ${d.title}\n${d.body}`).join("\n\n");

// SHA-256 of the canonical disclosure text. Used in the
// `disclosures_acknowledged` event payload (UX §4.1). Browser-side
// implementation via SubtleCrypto; backend will re-compute on receipt
// to ensure the user actually saw the current text.
export const computeDisclosureHash = async (): Promise<string> => {
  const text = canonicalDisclosureText();
  const buf = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};
